import { Flex, Heading, Text } from '@radix-ui/themes';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineBurnInFiringTestHoC } from 'components/sections/admin-portal/machines/dialogs/settings/machine-burn-in/firing-test';
import { MachineBurnInRangeTest } from 'components/sections/admin-portal/machines/dialogs/settings/machine-burn-in/range-test';
import { MachineBurnInReport } from 'components/sections/admin-portal/machines/dialogs/settings/machine-burn-in/report';
import useBurnInTestStore from 'components/sections/admin-portal/machines/dialogs/settings/machine-burn-in/use-burn-in-test-store';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachineContext } from 'contexts/machine.context';
import { QueueState } from 'lib_ts/enums/machine-msg.enum';
import { BurnInTestType } from 'lib_ts/enums/machine.enums';
import { ReactNode, useContext, useMemo } from 'react';
import useMachineStatusStore from 'stores/use-machine-status-store';

const COMPONENT_NAME = 'MachineBurnInRouter';

const FallbackWrapper = (props: { heading: string; children: ReactNode }) => {
  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <Flex gap="2" mb="1" align="center">
        <CommonBackArrow
          onClick={() => useBurnInTestStore.setState({ activeTestID: null })}
        />

        <Heading>{props.heading}</Heading>
      </Flex>
      {props.children}
    </ErrorBoundary>
  );
};

// determines what to render given a selected test
export const MachineBurnInRouter = () => {
  const queueState = useMachineStatusStore((s) => s.queueState);

  const apMachine = useMachinesPortalStore((s) => s.machine);
  const { machine: activeMachine } = useContext(MachineContext);

  const tests = useBurnInTestStore((s) => s.tests);
  const activeTestID = useBurnInTestStore((s) => s.activeTestID);

  const activeTest = useMemo(
    () => tests.find((m) => m._id === activeTestID),
    [tests, activeTestID]
  );

  if (!activeTest) {
    // user has not made a selection yet
    return null;
  }

  if (activeTest.end) {
    // completed test can be reviewed by anyone (not just the active user)
    return <MachineBurnInReport />;
  }

  if (!apMachine) {
    // shouldn't trigger
    console.warn(`${COMPONENT_NAME}: no admin portal machine detected`);
    return (
      <FallbackWrapper heading="No Machine">
        <CommonCallout
          content={
            <Text>
              Please select a machine from the Admin Portal and try again.
            </Text>
          }
        />
      </FallbackWrapper>
    );
  }

  if (apMachine.machineID !== activeMachine.machineID) {
    // admin is not connected to the machine
    return (
      <FallbackWrapper heading="Machine Mismatch">
        <CommonCallout
          content={
            <Text>
              You must be connected to {apMachine.machineID} to perform this
              test.
            </Text>
          }
        />
      </FallbackWrapper>
    );
  }

  if (queueState === QueueState.MachineDC) {
    // machine is offline
    return (
      <FallbackWrapper heading="Offline Machine">
        <CommonCallout
          content={
            <Text>
              {apMachine.machineID} must be online to perform this test.
            </Text>
          }
        />
      </FallbackWrapper>
    );
  }

  if (queueState !== QueueState.Active) {
    // admin is not active on the machine
    return (
      <FallbackWrapper heading="Inactive User">
        <CommonCallout
          content={
            <Text>
              You must be the active user on {apMachine.machineID} to perform
              this test.
            </Text>
          }
        />
      </FallbackWrapper>
    );
  }

  switch (activeTest.type) {
    case BurnInTestType.Firing: {
      return <MachineBurnInFiringTestHoC />;
    }

    case BurnInTestType.Range: {
      return <MachineBurnInRangeTest />;
    }

    default: {
      // shouldn't trigger
      return (
        <FallbackWrapper heading="Invalid Test">
          <CommonCallout
            content={
              <Text>Please create a new burn-in test and try again.</Text>
            }
          />
        </FallbackWrapper>
      );
    }
  }
};
